import { formatCurrency } from '../../PolicyAdmin/utils';
import type { DimensionSlice } from '../types';
import { colorForIndex } from './chartColors';

export interface BarListProps {
  slices: DimensionSlice[];
  /** Noun for the count shown next to each bar, e.g. "policies". */
  countNoun?: string;
  emptyMessage?: string;
}

/**
 * Ranked horizontal bars. Bars are scaled against the largest slice rather than
 * the total, so a long tail stays legible instead of collapsing to hairlines.
 */
export const BarList: React.FC<BarListProps> = ({
  slices,
  countNoun = 'policies',
  emptyMessage = 'No data for this period.',
}) => {
  if (!slices.length) {
    return (
      <p className="py-8 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        {emptyMessage}
      </p>
    );
  }

  const peak = Math.max(...slices.map(slice => Math.abs(slice.premium)), 1);

  return (
    <ul className="space-y-3.5">
      {slices.map((slice, index) => (
        <li key={slice.key}>
          <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
            <span
              className="min-w-0 truncate font-medium"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {slice.label}
            </span>
            <span
              className="flex-shrink-0 tabular-nums"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {formatCurrency(slice.premium)}
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full"
            style={{ backgroundColor: 'var(--color-hover-background)' }}
            role="img"
            aria-label={`${slice.label}: ${formatCurrency(slice.premium)}, ${slice.sharePct.toFixed(1)} percent of premium, ${slice.count} ${countNoun}`}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(2, (Math.abs(slice.premium) / peak) * 100)}%`,
                backgroundColor: colorForIndex(index, slice.key),
              }}
            />
          </div>
          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {slice.sharePct.toFixed(1)}% of premium · {slice.count} {countNoun}
          </p>
        </li>
      ))}
    </ul>
  );
};
